import { useEffect, useState, useCallback, useMemo } from "react";
import { useProject } from "@/contexts/ProjectContext";
import api from "@/lib/api";
import { formatDate, todayDhaka } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Calendar, AlertTriangle, MessageSquare, ListChecks, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["Normal", "Important", "Urgent"];

const priorityStyle = (p) => {
  if (p === "Urgent") return "bg-rose-50 text-rose-700 border-rose-200";
  if (p === "Important") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const emptyTask = (group_id) => ({
  title: "", description: "", group_id, assigned_to: "unassigned",
  priority: "Normal", tags: [], due_date: "", calendar_date: "", checklist: [],
});

export default function Tasks() {
  const { selected } = useProject();
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ assigned_to: "all", priority: "all", tag: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultGroup, setDefaultGroup] = useState(null);
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);

  const load = useCallback(async () => {
    if (!selected) return;
    const params = {};
    if (filters.assigned_to && filters.assigned_to !== "all") params.assigned_to = filters.assigned_to;
    if (filters.priority && filters.priority !== "all") params.priority = filters.priority;
    if (filters.tag) params.tag = filters.tag;
    const [g, t, u] = await Promise.all([
      api.get(`/projects/${selected.id}/task-groups`),
      api.get(`/projects/${selected.id}/tasks`, { params }),
      api.get(`/users`),
    ]);
    setGroups(g.data);
    setTasks(t.data);
    setUsers(u.data);
  }, [selected, filters]);

  useEffect(() => { load(); }, [load]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const tasksByGroup = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = [];
    for (const t of tasks) {
      if (!m[t.group_id]) m[t.group_id] = [];
      m[t.group_id].push(t);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    return m;
  }, [tasks, groups]);

  const openCreate = (group_id) => {
    setEditing(null);
    setDefaultGroup(group_id);
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setDefaultGroup(t.group_id);
    setOpen(true);
  };

  const onDragStart = (e, taskId) => {
    setDragTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => { setDragTaskId(null); setDragOverGroup(null); };
  const onDragOver = (e, gid) => {
    e.preventDefault();
    setDragOverGroup(gid);
  };
  const onDrop = async (e, gid) => {
    e.preventDefault();
    if (!dragTaskId) return;
    const task = tasks.find((t) => t.id === dragTaskId);
    if (!task) return;
    const targetOrder = (tasksByGroup[gid]?.length || 0);
    // optimistic update
    setTasks((prev) => prev.map((t) => t.id === dragTaskId ? { ...t, group_id: gid, order: targetOrder } : t));
    setDragOverGroup(null);
    setDragTaskId(null);
    try {
      await api.post(`/projects/${selected.id}/tasks/${dragTaskId}/move`, { group_id: gid, order: targetOrder });
    } catch (e) {
      toast.error("Failed to move task");
      load();
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    await api.post(`/projects/${selected.id}/task-groups`, { name: newGroupName.trim() });
    setNewGroupName("");
    setAddGroupOpen(false);
    toast.success("Column added");
    load();
  };
  const deleteGroup = async (gid) => {
    if (!confirm("Delete this column and all its tasks?")) return;
    await api.delete(`/projects/${selected.id}/task-groups/${gid}`);
    toast.success("Column deleted");
    load();
  };

  if (!selected) return <div className="text-sm text-slate-500">Select a project first.</div>;

  return (
    <div className="space-y-6 max-w-full" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Task Board</h1>
          <p className="text-sm text-slate-500 mt-1">Drag and drop tasks across columns</p>
        </div>
        <Button variant="outline" onClick={() => setAddGroupOpen(true)} data-testid="add-column-button">
          <Plus className="h-4 w-4 mr-2" /> Add Column
        </Button>
      </div>

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Assignee</Label>
            <Select value={filters.assigned_to} onValueChange={(v) => setFilters({ ...filters, assigned_to: v })}>
              <SelectTrigger className="w-48" data-testid="task-filter-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Priority</Label>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger className="w-40" data-testid="task-filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tag</Label>
            <Input value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })} placeholder="tag" className="w-32" data-testid="task-filter-tag" />
          </div>
          <Button variant="outline" onClick={() => setFilters({ assigned_to: "all", priority: "all", tag: "" })}>Clear</Button>
        </CardContent>
      </Card>

      <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll items-start">
        {groups.map((g) => {
          const groupTasks = tasksByGroup[g.id] || [];
          const isOver = dragOverGroup === g.id;
          return (
            <div
              key={g.id}
              onDragOver={(e) => onDragOver(e, g.id)}
              onDrop={(e) => onDrop(e, g.id)}
              onDragLeave={() => setDragOverGroup((curr) => curr === g.id ? null : curr)}
              className={`w-80 flex-shrink-0 flex flex-col gap-3 bg-slate-100/60 p-3 rounded-2xl border ${isOver ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200/60"}`}
              data-testid={`kanban-column-${g.id}`}
            >
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-slate-800">{g.name}</span>
                  <Badge variant="secondary" className="text-[10px]">{groupTasks.length}</Badge>
                </div>
                <div className="flex items-center">
                  <Button size="sm" variant="ghost" onClick={() => openCreate(g.id)} data-testid={`add-task-${g.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteGroup(g.id)} data-testid={`delete-column-${g.id}`}><X className="h-3.5 w-3.5 text-slate-400" /></Button>
                </div>
              </div>
              <div className="flex flex-col gap-3 min-h-[40px]">
                {groupTasks.map((t) => {
                  const overdue = t.due_date && new Date(t.due_date) < new Date(todayDhaka()) && t.priority !== "Done";
                  return (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, t.id)}
                      onDragEnd={onDragEnd}
                      onClick={() => openEdit(t)}
                      className={`bg-white p-3 rounded-xl shadow-sm border border-slate-200 cursor-grab active:cursor-grabbing hover:border-indigo-300 transition-colors flex flex-col gap-2 ${dragTaskId === t.id ? "task-dragging" : ""}`}
                      data-testid={`task-card-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium text-slate-900 leading-snug">{t.title}</div>
                        {t.priority === "Urgent" && (
                          <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px] flex items-center gap-1 flex-shrink-0">
                            <AlertTriangle className="h-3 w-3" /> Urgent
                          </Badge>
                        )}
                      </div>
                      {t.description && <div className="text-xs text-slate-500 line-clamp-2">{t.description}</div>}
                      {t.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {t.tags.slice(0, 3).map((tag) => <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>)}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                        <div className="flex items-center gap-2">
                          {t.due_date && (
                            <span className={`flex items-center gap-1 ${overdue ? "text-rose-600 font-medium" : ""}`}>
                              <Calendar className="h-3 w-3" /> {formatDate(t.due_date)}
                            </span>
                          )}
                          {t.checklist?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <ListChecks className="h-3 w-3" /> {t.checklist.filter(c => c.done).length}/{t.checklist.length}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {t.priority !== "Normal" && t.priority !== "Urgent" && (
                            <Badge variant="outline" className={`text-[10px] ${priorityStyle(t.priority)}`}>{t.priority}</Badge>
                          )}
                          {t.assigned_to && userMap[t.assigned_to] && (
                            <span className="h-6 w-6 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-[10px] font-semibold">
                              {userMap[t.assigned_to].name?.substring(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {groupTasks.length === 0 && (
                  <div className="text-xs text-slate-400 px-3 py-6 text-center border border-dashed border-slate-200 rounded-xl">
                    Drop tasks here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TaskDialog
        open={open}
        onOpenChange={setOpen}
        projectId={selected.id}
        groups={groups}
        users={users}
        editing={editing}
        defaultGroup={defaultGroup}
        onSaved={load}
      />

      <Dialog open={addGroupOpen} onOpenChange={setAddGroupOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">New Column</DialogTitle>
          </DialogHeader>
          <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="e.g. Blocked" data-testid="new-column-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGroupOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={createGroup} data-testid="save-new-column">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskDialog({ open, onOpenChange, projectId, groups, users, editing, defaultGroup, onSaved }) {
  const [form, setForm] = useState(emptyTask(defaultGroup));
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [newChecklist, setNewChecklist] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || "",
        description: editing.description || "",
        group_id: editing.group_id,
        assigned_to: editing.assigned_to || "unassigned",
        priority: editing.priority || "Normal",
        tags: editing.tags || [],
        due_date: editing.due_date || "",
        calendar_date: editing.calendar_date || "",
        checklist: editing.checklist || [],
      });
      setTagsInput((editing.tags || []).join(", "));
      api.get(`/projects/${projectId}/tasks/${editing.id}/comments`).then((r) => setComments(r.data));
    } else {
      setForm(emptyTask(defaultGroup || groups[0]?.id));
      setTagsInput("");
      setComments([]);
    }
  }, [editing, open, defaultGroup, groups, projectId]);

  const addChecklist = () => {
    if (!newChecklist.trim()) return;
    setForm({
      ...form,
      checklist: [...form.checklist, { id: Math.random().toString(36).slice(2), text: newChecklist.trim(), done: false }],
    });
    setNewChecklist("");
  };
  const toggleChecklist = (id) => {
    setForm({
      ...form,
      checklist: form.checklist.map((c) => c.id === id ? { ...c, done: !c.done } : c),
    });
  };
  const removeChecklist = (id) => {
    setForm({ ...form, checklist: form.checklist.filter((c) => c.id !== id) });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        tags,
        assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to,
        due_date: form.due_date || null,
        calendar_date: form.calendar_date || null,
      };
      if (editing) {
        await api.patch(`/projects/${projectId}/tasks/${editing.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post(`/projects/${projectId}/tasks`, payload);
        toast.success("Task created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing || !confirm("Delete this task?")) return;
    await api.delete(`/projects/${projectId}/tasks/${editing.id}`);
    toast.success("Deleted");
    onOpenChange(false);
    onSaved?.();
  };

  const addComment = async () => {
    if (!newComment.trim() || !editing) return;
    const { data } = await api.post(`/projects/${projectId}/tasks/${editing.id}/comments`, { text: newComment.trim() });
    setComments([...comments, data]);
    setNewComment("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit Task" : "New Task"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="task-form-title" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-desc" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Column</Label>
              <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                <SelectTrigger data-testid="task-form-group"><SelectValue /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assignee</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger data-testid="task-form-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tags (comma)</Label>
              <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="design, frontend" data-testid="task-form-tags" />
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-form-due" />
            </div>
            <div className="space-y-2">
              <Label>Calendar Date</Label>
              <Input type="date" value={form.calendar_date} onChange={(e) => setForm({ ...form, calendar_date: e.target.value })} data-testid="task-form-calendar" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2"><ListChecks className="h-4 w-4" /> Checklist</Label>
            <div className="space-y-2">
              {form.checklist.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <Checkbox checked={c.done} onCheckedChange={() => toggleChecklist(c.id)} />
                  <span className={`flex-1 text-sm ${c.done ? "line-through text-slate-400" : "text-slate-700"}`}>{c.text}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeChecklist(c.id)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={newChecklist} onChange={(e) => setNewChecklist(e.target.value)} placeholder="Add checklist item" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChecklist(); } }} data-testid="checklist-input" />
                <Button type="button" variant="outline" onClick={addChecklist} data-testid="checklist-add">Add</Button>
              </div>
            </div>
          </div>

          {editing && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Comments</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {comments.map((c) => (
                  <div key={c.id} className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="text-xs text-slate-500 mb-1">{c.user_name} • {formatDate(c.created_at)}</div>
                    <div className="text-sm text-slate-700">{c.text}</div>
                  </div>
                ))}
                {comments.length === 0 && <div className="text-xs text-slate-400">No comments yet.</div>}
              </div>
              <div className="flex gap-2">
                <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(); } }} data-testid="comment-input" />
                <Button type="button" variant="outline" onClick={addComment} data-testid="comment-add">Post</Button>
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between sm:justify-between">
            {editing ? (
              <Button type="button" variant="outline" className="text-rose-600" onClick={remove} data-testid="task-form-delete">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="task-form-save">
                {saving ? "Saving..." : editing ? "Save" : "Create"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
